import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Grid,
  IconButton,
  InputBase,
  Paper,
  Tooltip,
  ArrowBackIosNewOutlined as ArrowBackIosNewOutlinedIcon,
  Close as CloseIcon,
} from "../../../common/utils/mui-imports";
import { SearchNormal1 } from "iconsax-react";
import InspectionOverviewFilterSection from "../../../components/common/InspectionAssignmentOverviewComponents/InspectionOverviewFilterSection";
import GridLayout from "../../../components/common/InspectionAssignmentOverviewComponents/GridLayout";
import { PageRegistrationProperties } from "../../../core/reducers/pageRegistrationReducer.types";
import usePageRegistration from "../../../core/hooks/usePageRegistration";
import { useAppDispatch, useAppSelector } from "../../../core/hooks";
import { InspectionAssignmentOverviewPageType } from "./InspectionAssignmentOverviewPage.types";
import {
  InspectionAssignmentOverviewDTO,
  PaginationDTO,
  SiteDTO,
  StatusSummaryCountDTO,
} from "../../../common/types/common.dto.types";
import {
  EnumListShowRowCountList,
  EnumSortTypes,
} from "../../../common/enums/common.enums";
import { inspectionAssignmentOverviewReducerActions } from "./InspectionAssignmentOverviewPage.reducer";
import { getAssignedInspections } from "./InspectionAssignmentOverviewPage.action";
import InspectionAssignmentOverviewStatus from "../../../components/common/InspectionAssignmentOverviewComponents/InspectionAssignmentOverviewStatus";

// Mock data types - replace with your actual types
interface AssessmentTypeDTO {
  GroupId: string;
  Code: string;
}

interface StatusStatisticsDTO {
  Id: number;
  StatusName: string;
  Count: number;
}

const initialState = {
  Data: {
    Data: [] as Array<InspectionAssignmentOverviewDTO>,
    PageSize: EnumListShowRowCountList.ListShowRowCountPerPage_3,
    SelectedPage: 1,
    StatusSummaryCount: {} as StatusSummaryCountDTO,
  } as PaginationDTO,
  InspectionAssignmentOverviewSearchParameter: {
    InspectionStatusId: 0,
    BasicSearchValue: "",
    AssessmentTypeGroupId: "0",
    SelectedPage: 1,
    ListPageSize: EnumListShowRowCountList.ListShowRowCountPerPage_3,
    ShowCompletedInspection: false,
    SelectedSiteIds: [] as Array<number>,
    PlannedCompletionDateSortState: EnumSortTypes.Default,
  } as InspectionAssignmentOverviewDTO,

  InspectionAssignmentOverviewStatusSummary: [] as Array<StatusStatisticsDTO>,
  InspectionAssessmentSummary: [] as Array<AssessmentTypeDTO>,
  InspectionAssignmentOverviewYears: [] as Array<number>,
  InspectionSiteList: [] as Array<SiteDTO>,
  DataLoading: false,
  SearchTerm: "",
} as InspectionAssignmentOverviewPageType;

const InspectionAssignmentOverview = (props: PageRegistrationProperties) => {
  usePageRegistration(props);

  const dataFetchedRef = useRef(false);
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  // create state for InspectionAssignmentOverviewPage with initialState values
  const [
    inspectionAssignmentOverviewPageState,
    setInspectionAssignmentOverviewPageState,
  ] = useState<InspectionAssignmentOverviewPageType>(initialState);

  const inspectionAssignmentOverviewState = useAppSelector(
    (state) => state.inspectionAssignmentOverviewReducer
  );

  // pass parameters from outside to page
  const location = useLocation();
  // State management
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);

  // Refs for height calculation
  const wrapperCard = useRef(null);
  const searchRef = useRef(null);
  const statusFilter = useRef(null);
  const assessmentTypeRef = useRef(null);
  const filterRef = useRef(null);

  // Mouse hover handlers
  const handleMouseEnter = () => {
    setIsMouseOver(true);
  };

  const handleMouseLeave = () => {
    setIsMouseOver(false);
  };

  /**
   * @name getAssignedInspectionsWithSearchParameters
   * @param {InspectionAssignmentOverviewDTO} searchParameter - The search parameters for fetching assigned inspections.
   * @description Fetches assigned inspections based on the provided search parameters.
   * @returns void
   */
  const getAssignedInspectionsWithSearchParameters = (
    searchParameter: InspectionAssignmentOverviewDTO
  ) => {
    dispatch(getAssignedInspections(searchParameter));
  };

  /**
   * @name setSearchParametersToState
   * @param searchParameter
   * @description Set the search parameters for inspection assignments in the page state.
   * @returns void
   */
  const setSearchParametersToState = (
    searchParameter: InspectionAssignmentOverviewDTO,
    enteredSearchTerm?: string
  ) => {
    setInspectionAssignmentOverviewPageState((values) => {
      return {
        ...values,
        InspectionAssignmentOverviewSearchParameter: searchParameter,
        SearchTerm:
          enteredSearchTerm ?? inspectionAssignmentOverviewPageState.SearchTerm,
      };
    });
  };

  /**
   * @description Calculates the height of the content area dynamically based on the wrapper card and other elements.
   * This ensures that the content area is scrollable and fits well within the available space.
   * It listens for window resize events to adjust the height accordingly.
   */
  useEffect(() => {
    const calculateHeight = () => {
      if (wrapperCard.current && searchRef.current && statusFilter.current) {
        const assessmentTypeHeight = assessmentTypeRef?.current
          ? assessmentTypeRef.current.offsetHeight + 15
          : 0;

        const filterHeight = filterRef?.current
          ? filterRef.current.offsetHeight + 10
          : 0;

        const calculatedHeight =
          wrapperCard.current.offsetHeight -
          searchRef.current.offsetHeight -
          statusFilter.current.offsetHeight -
          assessmentTypeHeight -
          filterHeight -
          40; // Extra padding

        setContentHeight(Math.max(calculatedHeight, 300)); // Minimum height of 300px
      }
    };

    calculateHeight();

    const handleResize = () => calculateHeight();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [inspectionAssignmentOverviewPageState.Data?.Data]);

  /**
   * @description Initializes the page state and fetches the assigned inspections when the component mounts.
   */
  useEffect(() => {
    if (dataFetchedRef.current) return;
    dataFetchedRef.current = true;

    setInspectionAssignmentOverviewPageState(initialState);
    dispatch(
      inspectionAssignmentOverviewReducerActions.resetAssignedInspections()
    );

    const searchParameterLocalStorage = JSON.parse(
      localStorage.getItem("inspectionAssignmentOverviewSearchParameter")
    );

    const initialStateWithSearchParameter = {
      ...initialState,
      InspectionAssignmentOverviewSearchParameter: {
        ...initialState.InspectionAssignmentOverviewSearchParameter,
        BasicSearchValue:
          searchParameterLocalStorage?.BasicSearchValue ??
          initialState.InspectionAssignmentOverviewSearchParameter
            .BasicSearchValue,
        AssessmentTypeGroupId:
          searchParameterLocalStorage?.AssessmentTypeGroupId ??
          initialState.InspectionAssignmentOverviewSearchParameter
            .AssessmentTypeGroupId,
        InspectionStatusId:
          searchParameterLocalStorage?.InspectionStatusId ??
          initialState.InspectionAssignmentOverviewSearchParameter
            .InspectionStatusId,

        ShowCompletedInspection:
          searchParameterLocalStorage?.ShowCompletedInspection ??
          initialState.InspectionAssignmentOverviewSearchParameter
            .ShowCompletedInspection,

        SelectedSiteIds:
          searchParameterLocalStorage?.SelectedSiteIds ??
          initialState.InspectionAssignmentOverviewSearchParameter
            .SelectedSiteIds,

        PlannedCompletionDateSortState:
          searchParameterLocalStorage?.PlannedCompletionDateSortState ??
          initialState.InspectionAssignmentOverviewSearchParameter
            .PlannedCompletionDateSortState,
      },
      SearchTerm:
        searchParameterLocalStorage?.BasicSearchValue ??
        initialState.InspectionAssignmentOverviewSearchParameter
          .BasicSearchValue,
    } as InspectionAssignmentOverviewPageType;

    setInspectionAssignmentOverviewPageState(initialStateWithSearchParameter);

    getAssignedInspectionsWithSearchParameters({
      ...initialStateWithSearchParameter.InspectionAssignmentOverviewSearchParameter,
    } as InspectionAssignmentOverviewDTO);

    //clear parameter
    navigate(location.pathname, { replace: true });
  }, []);

  /**
   * @description Updates the page state whenever the inspection assignment overview state changes.
   */
  useEffect(() => {
    if (!dataFetchedRef.current) return;
    setInspectionAssignmentOverviewPageState((values) => {
      return {
        ...values,
        Data: {
          ...values.Data,
          Data: inspectionAssignmentOverviewState.Data?.Data,
          SelectedPage: inspectionAssignmentOverviewState.Data?.SelectedPage,
          PageSize: inspectionAssignmentOverviewState.Data?.PageSize,
        } as PaginationDTO,

        InspectionAssignmentOverviewStatusSummary:
          inspectionAssignmentOverviewState.InspectionAssignmentOverviewStatusSummary,
        InspectionAssessmentSummary:
          inspectionAssignmentOverviewState.InspectionAssessmentSummary,
        InspectionAssignmentOverviewYears:
          inspectionAssignmentOverviewState.InspectionAssignmentOverviewYears,
        InspectionSiteList:
          inspectionAssignmentOverviewState.InspectionSiteList,

        InspectionAssignmentOverviewSearchParameter: {
          ...values?.InspectionAssignmentOverviewSearchParameter,
          SelectedPage: inspectionAssignmentOverviewState.Data?.SelectedPage,
          ListPageSize: inspectionAssignmentOverviewState.Data?.PageSize,

          InspectionStatusId:
            inspectionAssignmentOverviewState
              .InspectionAssignmentOverviewSearchParameter?.InspectionStatusId,
          AssessmentTypeGroupId:
            inspectionAssignmentOverviewState
              .InspectionAssignmentOverviewSearchParameter
              ?.AssessmentTypeGroupId,
        } as InspectionAssignmentOverviewDTO,
        SearchTerm:
          inspectionAssignmentOverviewState
            .InspectionAssignmentOverviewSearchParameter?.BasicSearchValue,
      } as InspectionAssignmentOverviewPageType;
    });
  }, [inspectionAssignmentOverviewState]);

  /**
   * @description Updates the local storage with the current search parameters
   * whenever the search parameters change. This allows the application to persist search state across sessions.
   */
  useEffect(() => {
    // add to local storage when search paramter change
    localStorage.setItem(
      "inspectionAssignmentOverviewSearchParameter",
      JSON.stringify(
        inspectionAssignmentOverviewState.InspectionAssignmentOverviewSearchParameter
      )
    );
  }, [
    inspectionAssignmentOverviewState.InspectionAssignmentOverviewSearchParameter,
  ]);

  //--------------------- Page Navigate <Start> ------------------------------//

  /**
   * @name onClickBackButton
   * @description Navigate back to main menu
   * @returns void
   */
  const onClickBackButton = () => {
    navigate("/");
  };

  //--------------------- Page Navigate <End> ------------------------------//

  //--------------------- Page Function Search <Start> ------------------------------//

  /**
   * @name searchAssignedInspections
   * @param searchParamter
   * @description Searches for assigned inspections based on the provided search parameters.
   * @returns void
   */
  const searchAssignedInspections = (
    searchParamter: InspectionAssignmentOverviewDTO
  ) => {
    setInspectionAssignmentOverviewPageState((values) => {
      return {
        ...values,
        InspectionAssignmentOverviewSearchParameter: searchParamter,
      };
    });

    getAssignedInspectionsWithSearchParameters(searchParamter);
  };

  /**
   * @name onClickBasicSearchBtn
   * @description Handles the click event for the basic search button.
   * @returns void
   */
  const onClickBasicSearchBtn = () => {
    if (
      inspectionAssignmentOverviewPageState.SearchTerm !==
      inspectionAssignmentOverviewState
        .InspectionAssignmentOverviewSearchParameter.BasicSearchValue
    ) {
      const inspectionAssignmentOverviewSearchParamter = {
        ...inspectionAssignmentOverviewPageState.InspectionAssignmentOverviewSearchParameter,
        BasicSearchValue: inspectionAssignmentOverviewPageState.SearchTerm,
        InspectionStatusId: 0,
        SelectedSiteIds: [],
      } as InspectionAssignmentOverviewDTO;

      searchAssignedInspections(inspectionAssignmentOverviewSearchParamter);
    }
  };

  /**
   * @name onChangeSearchName
   * @param searchValue
   * @description Handles the change event for the search input field.
   * @returns void
   */
  const onChangeSearchName = (searchValue: string) => {
    setInspectionAssignmentOverviewPageState((values) => {
      return {
        ...values,
        InspectionAssignmentOverviewSearchParameter: {
          ...values.InspectionAssignmentOverviewSearchParameter,
          BasicSearchValue: "",
        },
        SearchTerm: searchValue,
      };
    });

    if (!searchValue || searchValue === "") {
      const inspectionAssignmentOverviewSearchParamter = {
        ...inspectionAssignmentOverviewPageState.InspectionAssignmentOverviewSearchParameter,
        BasicSearchValue: "",
      } as InspectionAssignmentOverviewDTO;

      searchAssignedInspections(inspectionAssignmentOverviewSearchParamter);
    }
  };

  /**
   * @name onKeyPressSearchName
   * @param event
   * @description Handles the key press event for the search input field.
   * @returns void
   */
  const onKeyPressSearchName = (event: any) => {
    if (event.charCode === 13) {
      event.preventDefault();
      onClickBasicSearchBtn();
    }
  };

  /**
   * @name onClickInspectionStatusStatistic
   * @param statusId
   * @description Handles the click event on an inspection status statistic.
   * @returns void
   */
  const onClickInspectionStatusStatistic = (statusId: number) => {
    const inspectionSearchParamters = {
      ...inspectionAssignmentOverviewPageState.InspectionAssignmentOverviewSearchParameter,
      InspectionStatusId: statusId,
    } as InspectionAssignmentOverviewDTO;

    setSearchParametersToState(inspectionSearchParamters);
    getAssignedInspectionsWithSearchParameters(inspectionSearchParamters);
  };

  /**
   * @name onClickInspectionAssessmentType
   * @param assessmentTypeGroupId
   * @description Handles the click event on an assessment type group.
   * @returns void
   */
  const onClickInspectionAssessmentType = (assessmentTypeGroupId: string) => {
    const inspectionSearchParamters = {
      ...inspectionAssignmentOverviewPageState.InspectionAssignmentOverviewSearchParameter,
      AssessmentTypeGroupId: assessmentTypeGroupId,
      InspectionStatusId: 0,
    } as InspectionAssignmentOverviewDTO;

    setSearchParametersToState(inspectionSearchParamters);
    getAssignedInspectionsWithSearchParameters(inspectionSearchParamters);
  };

  /**
   * @name onLocationFilterSearch
   * @param selectedLocations
   * @description Handles the location filter search.
   * @returns void
   */
  const onLocationFilterSearch = (selectedLocations: number[]) => {
    const inspectionSearchParamters = {
      ...inspectionAssignmentOverviewPageState.InspectionAssignmentOverviewSearchParameter,
      InspectionStatusId: 0,
      BasicSearchValue: "",
      SelectedSiteIds: selectedLocations,
    } as InspectionAssignmentOverviewDTO;

    setSearchParametersToState(inspectionSearchParamters, "");
    getAssignedInspectionsWithSearchParameters(inspectionSearchParamters);
  };

  /**
   * @name onShowCompletedSelectionChange
   * @param isChecked
   * @description Handles the change in the "Show Completed" selection.
   * @returns void
   */
  const onShowCompletedSelectionChange = (isChecked: boolean) => {
    const inspectionSearchParamters = {
      ...inspectionAssignmentOverviewPageState.InspectionAssignmentOverviewSearchParameter,
      InspectionStatusId: 0,
      ShowCompletedInspection: isChecked,
    } as InspectionAssignmentOverviewDTO;

    setSearchParametersToState(inspectionSearchParamters);
    getAssignedInspectionsWithSearchParameters(inspectionSearchParamters);
  };

  /**
   * @name onPlannedCompletionDateSort
   * @param sortType
   * @description Handles the change in the planned completion date sort.
   * @returns void
   */
  const onPlannedCompletionDateSort = (sortType: number) => {
    const inspectionSearchParamters = {
      ...inspectionAssignmentOverviewPageState.InspectionAssignmentOverviewSearchParameter,
      PlannedCompletionDateSortState: sortType,
    } as InspectionAssignmentOverviewDTO;

    setSearchParametersToState(inspectionSearchParamters);
    getAssignedInspectionsWithSearchParameters(inspectionSearchParamters);
  };

  //--------------------- Page Function Search <End> ------------------------------//

  return (
    <div
      className="content-section-card rem-tracker top-main-padding site-inspection-start"
      ref={wrapperCard}
      style={{
        height: "calc(100vh - 95px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Grid container style={{ flex: "0 0 auto" }}>
        {/* Header with Search and Export */}
        <div
          className="flex--container fx-wrap width100-p top-main-border mb-5 rem-btn-wrap"
          ref={searchRef}
        >
          <div className="flex__item--inherit pr-10 pl-10">
            <h2>
              <Button
                aria-label="back"
                className="sub-head-back-btn"
                onClick={onClickBackButton}
                title="Back to home page"
              >
                <ArrowBackIosNewOutlinedIcon className="" />
                <span className="body-bold secondary-color">Back</span>
              </Button>
            </h2>
          </div>
          <div className="global-header-wrap rem-trak-head-res">
            <h1 className="page_title">Inspection Assignment Overview</h1>
          </div>
          <div className="flex__item search-bar--xsmall mr-10 pr-20 fx-around">
            <Paper
              component="form"
              sx={{
                p: "2px 4px",
                display: "flex",
                alignItems: "center",
                width: "100%",
                marginBottom: {
                  xs: "20px",
                  md: 0,
                },
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <InputBase
                value={inspectionAssignmentOverviewPageState.SearchTerm}
                onChange={(event) => {
                  onChangeSearchName(event.target?.value);
                }}
                onKeyPress={onKeyPressSearchName}
                sx={{ ml: 1, flex: 1 }}
                placeholder="Search Inspection Assignments"
                inputProps={{ "aria-label": "search" }}
              />
              {isMouseOver &&
                inspectionAssignmentOverviewPageState.SearchTerm?.length >
                  0 && (
                  <Tooltip title="Clear" placement="top">
                    <IconButton
                      className="mr-0"
                      onClick={() => onChangeSearchName("")}
                    >
                      <CloseIcon
                        style={{ color: "#219ebc", fontSize: "20px" }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              <Tooltip title="Search" placement="top">
                <IconButton
                  type="button"
                  sx={{ p: "10px" }}
                  aria-label="search"
                  className="m-0"
                  onClick={onClickBasicSearchBtn}
                >
                  <SearchNormal1
                    variant="Outline"
                    className="ico-secondary"
                    size={18}
                  />
                </IconButton>
              </Tooltip>
            </Paper>
          </div>
        </div>

        <div className="inspection-overview-container pl-10 pr-10">
          {/* Assessment Type Filter */}
          <Grid container>
            {inspectionAssignmentOverviewPageState.InspectionAssessmentSummary
              ?.length > 0 && (
              <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                <Grid
                  container
                  className="assessment-type-wrap"
                  ref={assessmentTypeRef}
                >
                  <div
                    className="standart-status-change mb-5 d-flex"
                    style={{
                      borderRight: "unset",
                      borderLeft: "unset",
                      zIndex: "10",
                    }}
                  >
                    <span className="mr-10 ml-10">Type: </span>
                    <Grid container className="status-wrp">
                      {inspectionAssignmentOverviewPageState.InspectionAssessmentSummary?.map(
                        (assessmentType: AssessmentTypeDTO, index: number) => (
                          <div
                            className="standards-status"
                            key={`assessmentType_${index}`}
                            onClick={() =>
                              onClickInspectionAssessmentType(
                                assessmentType.GroupId
                              )
                            }
                          >
                            <span
                              className={`status-color all ${
                                inspectionAssignmentOverviewPageState
                                  .InspectionAssignmentOverviewSearchParameter
                                  ?.AssessmentTypeGroupId ===
                                assessmentType?.GroupId
                                  ? "active"
                                  : ""
                              }`}
                            >
                              {assessmentType.Code}
                            </span>
                          </div>
                        )
                      )}
                    </Grid>
                  </div>
                </Grid>
              </Grid>
            )}
          </Grid>

          {/* Status */}
          <InspectionAssignmentOverviewStatus
            statusStatistics={
              inspectionAssignmentOverviewPageState.InspectionAssignmentOverviewStatusSummary ??
              []
            }
            selectedStatus={
              inspectionAssignmentOverviewPageState
                .InspectionAssignmentOverviewSearchParameter
                ?.InspectionStatusId ?? 0
            }
            onStatusClick={onClickInspectionStatusStatistic}
            statusFilterRef={statusFilter}
          />
        </div>

        {/* Filter Section */}
        <div className=" pl-10 pr-10" style={{ width: "100%" }} ref={filterRef}>
          <InspectionOverviewFilterSection
            inspectionLocations={
              inspectionAssignmentOverviewPageState.InspectionSiteList ?? []
            }
            inspectionLocationIds={
              inspectionAssignmentOverviewPageState
                .InspectionAssignmentOverviewSearchParameter?.SelectedSiteIds ??
              []
            }
            sortState={
              inspectionAssignmentOverviewPageState
                .InspectionAssignmentOverviewSearchParameter
                .PlannedCompletionDateSortState
            }
            onApplyLocationFilters={onLocationFilterSearch}
            showCompleted={
              inspectionAssignmentOverviewPageState
                .InspectionAssignmentOverviewSearchParameter
                .ShowCompletedInspection
            }
            onShowCompletedChange={onShowCompletedSelectionChange}
            onPlannedCompletionDateSortChange={onPlannedCompletionDateSort}
          />
        </div>
      </Grid>

      {/* Scrollable Grid Layout Container */}
      <div
        className=" pl-10 pr-10"
        style={{
          height: `${contentHeight}px`,
          flex: "1 1 auto",
          borderRadius: "4px",
          overflowY: "auto",
        }}
      >
        <GridLayout
          inspectionLocations={
            inspectionAssignmentOverviewPageState.Data?.Data ?? []
          }
          inspectionYears={
            inspectionAssignmentOverviewPageState.InspectionAssignmentOverviewYears ??
            []
          }
        />
      </div>
    </div>
  );
};

export default InspectionAssignmentOverview;
